import { Suspense, lazy, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Loading from "components/Loading";
import useCancelRequestEstimate, { type CancelRequestPayload } from "hooks/api/useCancelRequestEstimate";
import useFindEstimates from "hooks/api/useFindEstimates";
import useGetOrder from "hooks/api/useGetOrder";
import { useSyncEffect } from "hooks/effects";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal } from "hooks/useModal";
import type { CancelRequestPresenterProps } from "pages/CancelRequest/types";
import { markCancelRequestRequestedNow } from "utils/cancelRequestSession";
import { isBrowser, isTablet } from "utils/deviceDetect";

const CancelRequestPresenter = lazy(() =>
  isBrowser
    ? import("pages/CancelRequest/pc/CancelRequestPresenter")
    : isTablet
      ? import("pages/CancelRequest/tb/CancelRequestPresenter")
      : import("pages/CancelRequest/sp/CancelRequestPresenter")
);

const CancelRequestContainer = () => {
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.orderId ?? "";
  const estimateId = params.estimateId ?? "";

  const { showErrorModal } = useErrorModalStatusContext();
  const { InfoModal, showInfoModal, hideInfoModal } = useInfoModal();
  const [isLoading, setIsLoading] = useState(false);
  const { cancelRequestEstimate } = useCancelRequestEstimate();

  const { order, error: orderError } = useGetOrder(orderId);
  const { estimates, error: estimateError } = useFindEstimates(orderId);

  useSyncEffect(() => {
    if (orderError || estimateError) {
      showErrorModal({ httpMethod: "get", onPostClose: () => navigate(-1) });
    }
  }, [estimateError, navigate, orderError, showErrorModal]);

  const estimate = useMemo(() => estimates?.find((e) => e.id === estimateId) ?? null, [estimates, estimateId]);

  const isDataReady = !!(order && estimate && order.location && estimate.location);

  const onCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (payload: CancelRequestPayload) => {
      if (!orderId || !estimateId) return;
      setIsLoading(true);
      try {
        const status = await cancelRequestEstimate(orderId, estimateId, payload);
        if (status === 200) {
          markCancelRequestRequestedNow(estimateId);
          showInfoModal();
        }
      } catch {
        showErrorModal({ httpMethod: "post" });
      } finally {
        setIsLoading(false);
      }
    },
    [cancelRequestEstimate, estimateId, orderId, showErrorModal, showInfoModal]
  );

  const onInfoModalComplete = useCallback(() => {
    hideInfoModal();
    if (isBrowser) {
      navigate(-1);
    } else {
      navigate(`/order/${orderId}`);
    }
  }, [hideInfoModal, navigate, orderId]);

  if (orderError || estimateError) {
    return null;
  }

  const presenterProps: CancelRequestPresenterProps = {
    order: order ?? null,
    estimate,
    isDataReady,
    isLoading,
    onSubmit,
    onCancel,
    onInfoModalComplete,
    InfoModal,
    infoModalTitle: "キャンセルを申請しました",
  };

  return (
    <Suspense fallback={<Loading />}>
      <CancelRequestPresenter {...presenterProps} />
    </Suspense>
  );
};

export default CancelRequestContainer;
